/**
 * Auth/session lifecycle helpers.
 *
 * Pure, testable logic backing:
 *  - terminal sign-out (local storage purge + hard navigation)
 *  - cross-tab sign-out propagation via the Supabase auth storage key
 *  - detection of a revoked/expired session on ordinary PostgREST calls
 *
 * NOTHING here makes an authorization decision. A 403 / RLS denial for an
 * otherwise valid session must never be treated as a session loss.
 */

/** Supabase persists the session under `sb-<project-ref>-auth-token`. */
export const SUPABASE_AUTH_STORAGE_PREFIX = 'sb-';
export const SUPABASE_AUTH_STORAGE_SUFFIX = '-auth-token';

export function isSupabaseAuthStorageKey(key: string | null | undefined): boolean {
  if (!key) return false;
  return (
    key.startsWith(SUPABASE_AUTH_STORAGE_PREFIX) &&
    (key.endsWith(SUPABASE_AUTH_STORAGE_SUFFIX) ||
      key.includes(`${SUPABASE_AUTH_STORAGE_SUFFIX}.`)) // chunked tokens: ...-auth-token.0
  );
}

/** Device-context blobs cleared on sign-out so the next user inherits nothing. */
export const LOCAL_CONTEXT_KEYS = [
  'recovery_session_active',
  'lysara_factory_login',
  'lysara_outlet_login',
] as const;

/** Sign-in entry point for the surface the user is currently on. */
export function resolveSignInRouteForPath(pathname: string | null | undefined): string {
  const path = pathname ?? '/';
  if (path.startsWith('/outlet-portal') || path.startsWith('/outlet-user-auth')) {
    return '/outlet-user-auth';
  }
  if (path.startsWith('/outlet-auth')) return '/outlet-auth';
  if (path.startsWith('/factory-portal') || path.startsWith('/factory-user-auth')) {
    return '/factory-user-auth';
  }
  if (path.startsWith('/factory-auth')) return '/factory-auth';
  return '/auth';
}

/** Paths that are already a sign-in surface — never redirect away from them. */
const SIGN_IN_PATHS = [
  '/auth',
  '/outlet-auth',
  '/outlet-user-auth',
  '/factory-auth',
  '/factory-user-auth',
  '/verify',
  '/setup-password',
  '/accept-invite',
  '/superadmin-login',
  '/track',
  '/guest-driver',
];

export function isSignInSurface(pathname: string | null | undefined): boolean {
  const path = pathname ?? '/';
  return SIGN_IN_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

export interface StorageEventLike {
  key: string | null;
  newValue: string | null;
}

/**
 * True when another tab removed (or blanked) the Supabase auth token, i.e. a
 * sign-out happened elsewhere. A value *change* (token refresh) is ignored.
 */
export function shouldPropagateSignOut(event: StorageEventLike): boolean {
  if (!isSupabaseAuthStorageKey(event.key)) return false;
  return event.newValue === null || event.newValue === '';
}

export interface SupabaseLikeError {
  status?: number | null;
  code?: string | null;
  message?: string | null;
  name?: string | null;
}

const REVOKED_MESSAGE_PATTERNS = [
  'jwt expired',
  'invalid jwt',
  'invalid claim',
  'session not found',
  'session_not_found',
  'refresh_token_not_found',
  'bad_jwt',
  'token is expired',
];

/**
 * Revoked / expired session detection for ordinary PostgREST + RPC failures.
 *
 * Deliberately narrow:
 *  - HTTP 401, or PostgREST `PGRST301` (JWT expired), or a GoTrue session error
 *  - never 403 and never `42501` (RLS / permission denial with a valid session)
 */
export function isRevokedSessionError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as SupabaseLikeError & { context?: { status?: number } };

  const status = err.status ?? err.context?.status ?? null;
  if (status === 403) return false;

  const code = (err.code ?? '').toString();
  if (code === '42501' || code === 'PGRST116') return false;
  if (code === 'PGRST301' || code === 'bad_jwt' || code === 'session_not_found') return true;

  if (status === 401) return true;

  const message = (err.message ?? '').toString().toLowerCase();
  if (!message) return false;
  // "permission denied for table x" is authorization, not session loss.
  if (message.includes('permission denied') || message.includes('row-level security')) {
    return false;
  }
  return REVOKED_MESSAGE_PATTERNS.some((p) => message.includes(p));
}

/**
 * Remove any residual Supabase auth token material from web storage so a
 * revoked session cannot be re-hydrated on the next page load. Safe to call
 * even when the SDK already removed its own key.
 */
export function purgeSupabaseAuthStorage(): string[] {
  const removed: string[] = [];
  if (typeof window === 'undefined') return removed;

  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      const keys: string[] = [];
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i);
        if (isSupabaseAuthStorageKey(key)) keys.push(key as string);
      }
      for (const key of keys) {
        store.removeItem(key);
        removed.push(key);
      }
    } catch {
      // Storage unavailable (private mode / brokered preview storage) — ignore.
    }
  }
  return removed;
}
